import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import type { AuthTokens, LoginRequest } from '@sellokart/shared';
import { useQueryClient } from '@tanstack/react-query';
import type { ApiClient } from '@/lib/api';
import type { SessionStore } from '@/lib/session';

export interface AuthState<TUser> {
  user: TUser | null;
  /** True while a refresh token is held. */
  isAuthenticated: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
}

export interface AuthPortalConfig<TUser> {
  /** Portal label, used only in the "used outside its provider" error. */
  name: string;
  session: SessionStore<TUser>;
  /** The client bound to `session` — never the other portal's. */
  client: ApiClient;
  loginPath: string;
  logoutPath: string;
  /**
   * Query-key prefixes this portal owns. Sign-out drops exactly these queries,
   * so one portal's logout never wipes the other portal's cached data — and
   * nothing the previous operator loaded stays in the cache for the next one.
   */
  queryKeyPrefixes: ReadonlyArray<readonly unknown[]>;
}

export interface AuthPortal<TUser> {
  Provider: (props: { children: ReactNode }) => ReactElement;
  useAuth: () => AuthState<TUser>;
}

/**
 * Builds an independent auth context for one portal. Both portals are mounted
 * at the same time; because each closes over its own session store and its own
 * API client, their credentials are fully isolated (docs/architecture.md §5).
 */
export function createAuthPortal<TUser>(config: AuthPortalConfig<TUser>): AuthPortal<TUser> {
  const { name, session, client, loginPath, logoutPath, queryKeyPrefixes } = config;
  const Context = createContext<AuthState<TUser> | null>(null);

  function Provider({ children }: { children: ReactNode }): ReactElement {
    const queryClient = useQueryClient();
    const [user, setUser] = useState<TUser | null>(() => session.getUser());
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => session.isAuthenticated());

    // The API client clears the session when a refresh fails; mirror that here
    // so the route guard reacts immediately.
    useEffect(
      () =>
        session.subscribe(() => {
          setUser(session.getUser());
          setIsAuthenticated(session.isAuthenticated());
        }),
      [],
    );

    const login = useCallback(async (credentials: LoginRequest) => {
      const response = await client.post<AuthTokens & { user: TUser }>(loginPath, credentials, {
        authenticated: false,
      });
      session.start(response, response.user);
    }, []);

    const logout = useCallback(async () => {
      const refreshToken = session.getRefreshToken();
      try {
        await client.post<void>(logoutPath, { refreshToken });
      } catch {
        // Signing out locally must succeed even if the server call does not.
      } finally {
        session.clear();
        for (const queryKey of queryKeyPrefixes) queryClient.removeQueries({ queryKey });
      }
    }, [queryClient]);

    const value = useMemo<AuthState<TUser>>(
      () => ({ user, isAuthenticated, login, logout }),
      [user, isAuthenticated, login, logout],
    );

    return <Context.Provider value={value}>{children}</Context.Provider>;
  }

  function useAuth(): AuthState<TUser> {
    const context = useContext(Context);
    if (context === null) throw new Error(`use${name}Auth must be used inside its provider.`);
    return context;
  }

  return { Provider, useAuth };
}
